import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
} from '@mui/material';
import {
    DateField,
    DeleteButton,
    EditButton,
    useNotify,
    useRecordContext,
    useRefresh,
} from 'react-admin';
import { useLocation } from 'react-router';
import { useState } from 'react';
import { MissionStatus, MissionSummary, setMissionStatus } from '@wwf/sdk';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import { SectionDivider } from '@app/misc/SectionDivider';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { MissionStatusChip } from '@app/mission/chips/MissionStatusChip';
import { ArchiveButton, UnarchiveButton } from './MissionArchiveButton';
import { CopyAppLinkButton } from '@app/misc/CopyAppLinkButton';
import { DuplicateButton } from '../../misc/DuplicateButton';
import { missionSummaryToDuplicateDefaults } from '../edit/missionDuplicateDefaults';

export const MissionShowAside = () => {
    const location = useLocation();
    const record = useRecordContext<MissionSummary>();
    if (!record) return null;
    return (
        <Stack ml={4}>
            <Stack
                direction={'row'}
                mb={2}
                flexWrap={'wrap'}
                justifyContent={'space-between'}
                gap={1}
            >
                <EditButton resource="mission" label="Edit Mission" />
                <DuplicateButton
                    label="Duplicate Mission"
                    resource="mission"
                    duplicateDefaults={missionSummaryToDuplicateDefaults(
                        record
                    )}
                />
                <CopyAppLinkButton type="mission" id={record.id} />
                {record.status === 'ARCHIVED' ? (
                    <UnarchiveButton record={record as MissionSummary} />
                ) : (
                    <ArchiveButton record={record as MissionSummary} />
                )}
            </Stack>

            <LabelValueRow
                label="Status"
                value={<MissionShowStatusSelect record={record} />}
            />

            <SectionDivider title="Antenna info" />

            <LabelValueRow
                label="Antenna"
                value={
                    <AntennaLightCard record={record.antenna ?? undefined} />
                }
            />
            <LabelValueRow
                label="Main Volunteer"
                value={<MemberLightCard record={record.contact ?? undefined} />}
            />

            <SectionDivider title="Additional info" />

            <LabelValueRow
                label="Created at"
                value={
                    <DateField
                        source="createdAt"
                        record={record}
                        color="textPrimary"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />

            <LabelValueRow
                label="Updated at"
                value={
                    <DateField
                        source="updatedAt"
                        record={record}
                        color="textPrimary"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />

            <DeleteButton
                resource="mission"
                redirect={location.state?.from || undefined}
            />
        </Stack>
    );
};

const MissionShowStatusSelect = ({ record }: { record: MissionSummary }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const { missionStatusLabels } = useConfigurationContext();
    const [pending, setPending] = useState(false);

    const handleStatusChange = (event: SelectChangeEvent<MissionStatus>) => {
        const next = event.target.value as MissionStatus;
        if (next === record.status) return;
        setPending(true);
        void setMissionStatus(record.id, { status: next })
            .then(() => {
                notify('Mission status updated', { type: 'info' });
                refresh();
            })
            .catch(error => {
                console.error(error);
                notify(
                    error instanceof Error
                        ? error.message
                        : 'Could not update mission status',
                    { type: 'error' }
                );
            })
            .finally(() => {
                setPending(false);
            });
    };

    return (
        <FormControl size="small" fullWidth disabled={pending}>
            <InputLabel id="mission-show-status-label">Status</InputLabel>
            <Select<MissionStatus>
                labelId="mission-show-status-label"
                label="Status"
                value={record.status}
                onChange={handleStatusChange}
                renderValue={value => <MissionStatusChip status={value} />}
            >
                {missionStatusLabels.map(s => (
                    <MenuItem key={s.value} value={s.value}>
                        <MissionStatusChip status={s.value} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
